import { Card, Chart, getColorFromCssVariable, HorizontalBarsChart } from "ui";
import { ChartSkeleton, HorizontalBarsChartSkeleton } from "./Skeletons";
import { useMoneyHashQuery } from "../../../hooks/useMoneyHashQuery";
import { axiosApiInstance } from "../../../pages/api/axios";
import { useEffect, useMemo } from "react";
import { Heading } from "./Heading";
import { ErrorPage } from "../../../components/ErrorPage";
import {
  MethodPoint,
  OrganizationPoint,
  ProviderPoint,
  SuccessAndAcceptanceMetrics,
} from "../types";
import { twoFixedDigit } from "../../../utils/twoFixedDigit";
import { useUserContext } from "../../../contexts/UserProvider";
import { format, isSameDay } from "date-fns";
import { Filters } from "../../../components/newFilters";
import { useRouter } from "next/router";

const dateFilters = ["start_date", "end_date"];

export const SuccessAndAcceptanceRates = () => {
  const router = useRouter();
  const { start_date, end_date, provider, account, initiation, organization } =
    router.query;
  const { isMoneyHashUser, organizationId } = useUserContext();

  const {
    data: successAndAcceptanceMetrics,
    isLoading,
    isError,
    error,
    listMeta,
  } = useMoneyHashQuery<SuccessAndAcceptanceMetrics>({
    queryKey: [
      "success-and-acceptance-rates",
      {
        start_date,
        end_date,
        organization,
        organizationId,
        provider,
        account,
        initiation,
      },
    ],
    queryFn: ({ signal }) =>
      getSuccessAndAcceptanceRates({
        startDate: start_date as string,
        endDate: end_date as string,
        selectedOrg: (organization || organizationId) as string,
        selectedProvider: provider as string,
        selectedAccountsIds: account as string[],
        selectedInitiation: initiation as string,
        signal,
      }),
    enabled: !isMoneyHashUser || (isMoneyHashUser && !!organizationId),
  });

  const showSkeleton = isLoading || isError || !successAndAcceptanceMetrics;
  const skeletonState = isLoading ? "loading" : "error";

  const isSameDayRange = useMemo(
    () =>
      isSameDay(
        start_date ? new Date(start_date as string) : new Date(),
        end_date ? new Date(end_date as string) : new Date()
      ),
    [start_date, end_date]
  );

  useEffect(() => {
    if ((!organization && isMoneyHashUser) || !start_date || !end_date) {
      router.replace({
        query: {
          ...router.query,
          ...(isMoneyHashUser &&
            !organization && { organization: organizationId }),
          ...(!start_date && {
            start_date: format(new Date(), "yyyy-MM-dd"),
          }),
          ...(!end_date && { end_date: format(new Date(), "yyyy-MM-dd") }),
        },
      });
    }
  }, [
    organizationId,
    isMoneyHashUser,
    organization,
    start_date,
    end_date,
    router,
  ]);

  useEffect(
    () => {
      if (isMoneyHashUser && organization) {
        const { account: _account, ...rest_params } = router.query;
        router.replace({
          query: {
            ...rest_params,
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organization, isMoneyHashUser]
  );

  return (
    <>
      <div className="my-6 flex flex-col justify-center gap-4 px-6">
        <h2 className="flex items-center text-lg font-medium text-foundations-boldest">
          Overview of your payment rates
        </h2>

        <Filters
          filters={listMeta?.filter_types || []}
          recentStoreKey={"dashboard-insights-filters"}
          customDateFilters={dateFilters}
          filtersFor="dashboard"
          nonClearableFilters={["organization"]}
          dependantFilter={
            isMoneyHashUser
              ? {
                  name: "account",
                  filterBy: "organization",
                }
              : undefined
          }
        />
      </div>

      {isError ? (
        <ErrorPage error={error} />
      ) : (
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {showSkeleton ? (
            <>
              <ChartSkeleton state={skeletonState} />
              <HorizontalBarsChartSkeleton state={skeletonState} />
              <HorizontalBarsChartSkeleton state={skeletonState} />
              <ChartSkeleton state={skeletonState} />
              <HorizontalBarsChartSkeleton state={skeletonState} />
              <HorizontalBarsChartSkeleton state={skeletonState} />
            </>
          ) : (
            <>
              <OrganizationRateMetrics
                type="success"
                data={
                  successAndAcceptanceMetrics?.rate_metrics
                    ?.organization_success_rates
                }
                isSameDayRange={isSameDayRange}
              />
              <ProviderRateMetrics
                type="success"
                data={
                  successAndAcceptanceMetrics?.rate_metrics
                    .provider_success_rates
                }
              />
              <MethodRateMetrics
                type="success"
                data={
                  successAndAcceptanceMetrics?.rate_metrics.method_success_rates
                }
              />

              <OrganizationRateMetrics
                type="acceptance"
                data={
                  successAndAcceptanceMetrics?.rate_metrics
                    ?.organization_acceptance_rates
                }
                isSameDayRange={isSameDayRange}
              />
              <ProviderRateMetrics
                type="acceptance"
                data={
                  successAndAcceptanceMetrics?.rate_metrics
                    .provider_acceptance_rates
                }
              />
              <MethodRateMetrics
                type="acceptance"
                data={
                  successAndAcceptanceMetrics?.rate_metrics
                    .method_acceptance_rates
                }
              />
            </>
          )}
        </div>
      )}
    </>
  );
};

type OrganizationChartData = {
  type: "success" | "acceptance";
  data: Array<OrganizationPoint>;
  isSameDayRange?: boolean;
};

const OrganizationRateMetrics = ({
  type,
  data,
  isSameDayRange,
}: OrganizationChartData) => {
  const [labels, values] = useMemo(
    () => [data.map((d) => d.time), data.map((d) => d.rate)],
    [data]
  );

  const successColor = getColorFromCssVariable("--chart-categorical-2-subtler");
  const acceptanceColor = getColorFromCssVariable("--chart-brand");

  return (
    <Card disableHover className="flex flex-col">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <Heading>Organization {type} rate</Heading>
        </div>
      </div>
      <div className="flex h-80 flex-col">
        <Chart
          key={labels.join("")}
          type="line"
          labels={labels}
          datasets={[
            {
              data: values,
              backgroundColor:
                type === "success" ? successColor : acceptanceColor,
              borderColor: type === "success" ? successColor : acceptanceColor,
            },
          ]}
          maxYValue={100}
          formatXLabel={(value) =>
            format(new Date(value), isSameDayRange ? "h a" : "MMM d")
          }
          tooltipLabelFormatter={(value) =>
            `${format(new Date(value), isSameDayRange ? "h a" : "MMM d")}`
          }
          tooltipValueFormatter={(value) => `${value}%`}
        />
      </div>
    </Card>
  );
};

type ProviderChartData = {
  type: "success" | "acceptance";
  data: Array<ProviderPoint>;
};

const ProviderRateMetrics = ({ type, data }: ProviderChartData) => {
  const successColor = "bg-[rgba(var(--chart-categorical-2-subtler))]";
  const acceptanceColor = "bg-[rgba(var(--chart-categorical-1-subtler))]";

  return (
    <Card disableHover className="-p-6 flex flex-col overflow-hidden pt-6">
      <div className="mb-4 px-6">
        <Heading>Provider {type} rate</Heading>
      </div>

      <div className="relative">
        <div className="flex h-96 flex-col overflow-y-scroll px-6 pb-10">
          <HorizontalBarsChart
            items={data.map((provider) => ({
              label: provider.provider,
              value: provider.rate,
              percentage: provider.rate,
              barClassName: type === "success" ? successColor : acceptanceColor,
            }))}
            formatValue={(item) => `${twoFixedDigit(item.percentage)}%`}
          />
        </div>

        <div className="pointer-events-none absolute bottom-0 left-0 z-10 h-16 w-full bg-gradient-to-t from-[theme(backgroundColor.foundations-default)] to-transparent" />
      </div>
    </Card>
  );
};

type MethodChartData = {
  type: "success" | "acceptance";
  data: Array<MethodPoint>;
};

const MethodRateMetrics = ({ type, data }: MethodChartData) => {
  const successColor = "bg-[rgba(var(--chart-categorical-2-subtler))]";
  const acceptanceColor = "bg-[rgba(var(--chart-categorical-1-subtler))]";

  return (
    <Card disableHover className="-p-6 flex flex-col overflow-hidden pt-6">
      <div className="mb-4 px-6">
        <Heading>Method {type} rate</Heading>
      </div>

      <div className="relative">
        <div className="flex h-96 flex-col overflow-y-scroll px-6 pb-10">
          <HorizontalBarsChart
            items={data.map((method) => ({
              label: method.method,
              value: method.rate,
              percentage: method.rate,
              barClassName: type === "success" ? successColor : acceptanceColor,
            }))}
            formatValue={(item) => `${twoFixedDigit(item.percentage)}%`}
          />
        </div>

        <div className="pointer-events-none absolute bottom-0 left-0 z-10 h-16 w-full overflow-hidden bg-gradient-to-t from-[theme(backgroundColor.foundations-default)] to-transparent" />
      </div>
    </Card>
  );
};

const getSuccessAndAcceptanceRates = async ({
  startDate,
  endDate,
  selectedOrg,
  selectedProvider,
  selectedAccountsIds,
  selectedInitiation,
  signal,
}: {
  startDate: string;
  endDate: string;
  selectedOrg: string | undefined;
  selectedProvider: string | undefined;
  selectedAccountsIds: string[] | undefined;
  selectedInitiation: string | undefined;
  signal?: AbortSignal;
}) => {
  const today = new Date().toISOString().split("T")[0];

  return axiosApiInstance
    .get("/d/metrics/success_acceptance_rates/", {
      signal,
      params: {
        start_date: startDate || today,
        end_date: endDate || today,
        organization: selectedOrg,
        provider: selectedProvider,
        account: selectedAccountsIds,
        initiation: selectedInitiation,
      },
    })
    .then((res) => res.data);
};
